import {AxiosResponse} from "axios";

import CoreApi from "@/src/modules/core/api";
import {axios, getParams} from "@/src/modules/core/lib";
import {type ParamsOptions} from "@/src/modules/core/types";

import {
  AcknowledgeShiftRequest,
  AcknowledgeShiftResponse,
  AddPatrolVisitRequest,
  AdminActionRequest,
  BulkCopyRequest,
  BulkCopyResponse,
  BulkScheduleRequest,
  CheckPatrolVisitRequest,
  CopyRequest,
  EmergencyShiftRequest,
  GetShiftCoverageResponse,
  GetShiftsResponse,
  GetTemplatesResponse,
  ScheduleRequest,
  Shift,
  ShiftPatrolVisit,
  ShiftRequest,
  SuggestShiftBreaksData,
  SuggestShiftBreaksParams,
  Template,
  TemplateRequest,
} from "../types";

class ShiftsApi extends CoreApi {
  getShifts(options: ParamsOptions): Promise<AxiosResponse<GetShiftsResponse>> {
    return super.search("shift", options);
  }

  getShiftCoverage(options: ParamsOptions = {}): Promise<AxiosResponse<GetShiftCoverageResponse>> {
    const params = getParams(options);

    return axios({
      url: "shift-coverage",
      method: "get",
      params,
    });
  }

  getShift(id: string | number, options?: ParamsOptions): Promise<AxiosResponse<{data: Shift}>> {
    return super.getById("shift", id, options);
  }

  createShift(data: ShiftRequest): Promise<AxiosResponse<{data: Shift}>> {
    return super.create("shift", data);
  }

  updateShift(shiftId: number, data: ShiftRequest): Promise<AxiosResponse<{data: Shift}>> {
    return super.update("shift", shiftId, data);
  }

  deleteShift(
    shiftId: number,
    notifyOfficer: boolean,
    forceDelete?: boolean,
  ): Promise<AxiosResponse<void>> {
    const data: {notify_officer: boolean; force_delete?: boolean} = {
      notify_officer: notifyOfficer,
    };
    if (forceDelete) {
      data.force_delete = true;
    }
    return super.delete("shift", shiftId, data);
  }

  getTemplates(options: ParamsOptions): Promise<AxiosResponse<GetTemplatesResponse>> {
    return super.search("shift-template", options);
  }

  getTemplateById(templateId: number): Promise<AxiosResponse<{data: Template}>> {
    return super.getById("shift-template", templateId);
  }

  createTemplate(data: TemplateRequest): Promise<AxiosResponse<{data: Template}>> {
    return super.create("shift-template", data);
  }

  updateTemplate(shiftId: number, data: TemplateRequest): Promise<AxiosResponse<{data: Template}>> {
    return super.update("shift-template", shiftId, data);
  }

  deleteTemplate(shiftId: number): Promise<AxiosResponse<void>> {
    return super.delete("shift-template", shiftId);
  }

  copy(data: CopyRequest): Promise<AxiosResponse<void>> {
    return super.create("shift/copy", data);
  }

  bulkCopy(data: BulkCopyRequest): Promise<AxiosResponse<BulkCopyResponse>> {
    return super.create("shift/bulk-copy", data);
  }

  schedule(data: ScheduleRequest): Promise<AxiosResponse<void>> {
    return super.create("shift/schedule", data);
  }

  bulkSchedule(data: BulkScheduleRequest): Promise<AxiosResponse<BulkCopyResponse>> {
    return super.create("shift/bulk-schedule", data);
  }

  bulkDelete(data: BulkCopyRequest): Promise<AxiosResponse<void>> {
    return super.create("shift/bulk-delete", data);
  }

  adminAction(data: AdminActionRequest) {
    return super.create("shift/admin-action", data);
  }

  createEmergencyShift(data: EmergencyShiftRequest) {
    return super.create("emergency-shift", data);
  }

  acknowledgeShift(
    data: AcknowledgeShiftRequest,
  ): Promise<AxiosResponse<AcknowledgeShiftResponse>> {
    return super.post("shift/acknowledge", data);
  }

  suggestShiftBreaks(
    params: SuggestShiftBreaksParams,
  ): Promise<AxiosResponse<{success: 1; message: string; data: SuggestShiftBreaksData}>> {
    return axios({
      url: "shift/suggest-breaks",
      method: "get",
      params,
    });
  }

  checkPatrolVisit(
    shiftId: number,
    visitId: number,
    data: CheckPatrolVisitRequest,
  ): Promise<AxiosResponse<{data: ShiftPatrolVisit}>> {
    return axios({
      url: `shift/${shiftId}/patrol-visit/${visitId}/check`,
      method: "post",
      data,
    });
  }

  addPatrolVisit(
    shiftId: number,
    data: AddPatrolVisitRequest,
  ): Promise<AxiosResponse<{data: ShiftPatrolVisit}>> {
    return axios({
      url: `shift/${shiftId}/patrol-visit`,
      method: "post",
      data,
    });
  }

  deletePatrolVisit(shiftId: number, visitId: number): Promise<AxiosResponse<void>> {
    return axios({
      url: `shift/${shiftId}/patrol-visit/${visitId}`,
      method: "delete",
    });
  }
}

export const shiftsApi = new ShiftsApi();
